import Phaser from 'phaser';
import { setupDiscord, discordSdk, getParticipants, subscribeParticipants, discordAvatarUrl } from './discord';
import { detectPlatform, consumeStoredToken, clearStoredToken } from './web-auth';
import { renderLoginScreen } from './ui/panels/LoginScreen';
import { APIManager, StateManager, EventBus, ScreenRouter } from './managers';
import { installGlobalErrorHandlers, startSession, snapshotLoadout } from './managers/Telemetry';
import { CombatScreen } from './ui/panels/CombatScreen';
import { MainMenuScreen } from './ui/panels/MainMenuScreen';
import { PartyScreen } from './ui/panels/PartyScreen';
import { CrawlsBrowserScreen } from './ui/panels/CrawlsBrowserScreen';
import { SoulloomScreen } from './ui/panels/SoulloomScreen';
import { CraftingScreen } from './ui/panels/CraftingScreen';
import { ShopScreen } from './ui/panels/ShopScreen';
import { CrawlDetailScreen } from './ui/panels/CrawlDetailScreen';
// CampaignScreen removed — campaign now lives inside CrawlsBrowserScreen
import { HeroSelectScreen } from './ui/panels/HeroSelectScreen';
import { InspectScreen } from './ui/panels/InspectScreen';
import { CodexScreen } from './ui/panels/CodexScreen';
import { ToastManager } from './ui/components/Toast';
import { Tooltip } from './ui/components/Tooltip';
import { NavBar } from './ui/components/NavBar';
import { CombatScene } from './combat';
import { BACKGROUNDS, ENEMY_SPRITES, PLAYER_SPRITES, CB } from './combat/spriteConfig';
import type { CombatSceneData, ServerCombatState, CrawlData } from './types';

let phaserGame: Phaser.Game | null = null;
let phaserInitPending = false;

function setLoadingStatus(text: string): void {
  const el = document.querySelector('.loading-status');
  if (el) el.textContent = text;
}

async function preloadImages(urls: string[]): Promise<void> {
  await Promise.all(urls.map(url => new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // don't block on missing images
    img.src = url;
  })));
}

async function initializeGame(): Promise<void> {
  console.log('[Main] Initializing game...');

  installGlobalErrorHandlers();

  setLoadingStatus('Connecting...');

  const platform = detectPlatform();
  console.log('[Main] Platform:', platform);
  let user: { id: string; username: string; avatar: string | null } | null = null;

  if (platform === 'steam') {
    const steamAuth = (window as unknown as { __STEAM_AUTH__?: { sessionToken: string } }).__STEAM_AUTH__;
    console.log('[Main] Steam client detected, using session token');
    APIManager.setAccessToken(steamAuth!.sessionToken);
  } else if (platform === 'discord-iframe') {
    const discordUser = await setupDiscord();
    if (discordUser) {
      user = discordUser;
      console.log('[Main] Discord user:', user.username);
    }
  } else {
    // Standalone web build — token via OAuth redirect to /auth/callback.
    // Local dev: backend uses devAuth (any request = DevPlayer), so skip the
    // login screen entirely. Production: require a real token.
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalDev) {
      console.log('[Main] Local dev: bypassing OAuth (backend uses devAuth)');
    } else {
      const token = consumeStoredToken();
      if (!token) {
        console.log('[Main] No token; showing login screen');
        renderLoginScreen();
        return;
      }
      APIManager.setAccessToken(token);
      console.log('[Main] Web mode: token loaded');
    }
  }

  // Fetch initial state
  setLoadingStatus('Loading world...');
  const result = await APIManager.fetchUnifiedState();
  if (!result.success || !result.data) {
    console.error('[Main] Failed to fetch state:', result.error);
    // In web mode, a 401 means the stored token is no longer valid — clear and re-prompt.
    // Skip on localhost (devAuth never 401s; failure there means backend isn't running).
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (platform === 'web' && !isLocalDev) {
      clearStoredToken();
      renderLoginScreen({ error: 'Session expired. Please sign in again.' });
      return;
    }
    showError('Failed to load game. Please refresh.');
    return;
  }

  // Update state with all data from server
  StateManager.batchUpdate({
    playerData: result.data.playerData,
    combatData: result.data.combatData,
    crawlData: result.data.crawlData,
    eventCrawlData: result.data.eventCrawlData,
    campaignData: result.data.campaignData,
    configData: result.data.configData,
    unitData: result.data.unitData,
    itemData: result.data.itemData,
    notificationData: result.data.notificationData,
    coachData: result.data.coachData,
    uiState: { activePanel: 'combat', isLoading: false },
    isAdmin: result.data.isAdmin ?? false,
    achievementData: result.data.achievementData,
  });

  // Wire achievement listener AFTER configData is present so the catalog
  // resolve in process() finds the def for every newly[] id.
  const { AchievementsManager } = await import('./managers');
  AchievementsManager.initialize();

  // Telemetry: start session + snapshot loadout once auth+state are loaded
  startSession();
  snapshotLoadout();

  // Fetch notification state (non-blocking)
  APIManager.getNotifications().then(notifResult => {
    if (notifResult.success && notifResult.data) {
      StateManager.set('notificationData', notifResult.data);
    }
  });

  // ── Initialize social presence ──
  if (discordSdk) {
    const participants = await getParticipants();
    StateManager.set('presenceData', {
      participants,
      self: user ? { id: user.id, username: user.username, avatar: user.avatar, global_name: null, nickname: undefined } : null,
    });

    subscribeParticipants((updated) => {
      const currentPresence = StateManager.get('presenceData') as import('./types').PresenceData | undefined;
      const oldIds = new Set((currentPresence?.participants ?? []).map(p => p.id));
      const selfId = currentPresence?.self?.id;

      // Toast for new arrivals
      for (const p of updated) {
        if (!oldIds.has(p.id) && p.id !== selfId) {
          ToastManager.show({
            type: 'social',
            message: `${p.global_name || p.username} joined`,
            duration: 4000,
            avatarUrl: discordAvatarUrl(p.id, p.avatar, 64),
          });
        }
      }

      StateManager.set('presenceData', {
        participants: updated,
        self: currentPresence?.self ?? null,
      });
    });
  } else {
    // No Discord SDK (web build, Steam build, or dev mode). Derive self from
    // playerData; in dev mode also seed mock participants so the presence bar
    // has something to show during local testing.
    const player = result.data.playerData as { discordId?: string | null; gameUsername?: string; username?: string } | null;
    const selfId = player?.discordId || 'self';
    const selfName = player?.gameUsername || player?.username || 'Player';
    const self = { id: selfId, username: selfName, avatar: null, global_name: selfName };

    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const participants = isLocalDev
      ? [
          self,
          { id: 'mock-user-1', username: 'Aelindra', avatar: null, global_name: 'Aelindra' },
          { id: 'mock-user-2', username: 'Stoneguard', avatar: null, global_name: 'Stoneguard' },
        ]
      : [self];

    StateManager.set('presenceData', { participants, self });
  }

  // ── Phase 1: Blocking preload (during loading screen) ──
  // Load everything needed for the first screen the player will see.
  setLoadingStatus('Preparing...');
  const base = import.meta.env.BASE_URL || '/';
  const preloadUrls: string[] = [
    `${base}sprites/processed/bg/town_menu.webp${CB}`,
    `${base}sprites/processed/images/fatewoven_logo_sm.webp${CB}`,
  ];
  // All roster unit portraits (party screen needs these)
  const unitData = result.data.unitData as { roster?: { spriteKey?: string; unitClass?: string }[] } | undefined;
  const configData = result.data.configData as { unitTemplates?: Record<string, { spriteKey?: string }> } | undefined;
  if (unitData?.roster && configData?.unitTemplates) {
    for (const unit of unitData.roster) {
      const tmpl = configData.unitTemplates[(unit as any).unitClass];
      const key = tmpl?.spriteKey;
      if (key) preloadUrls.push(`${base}sprites/processed/players/${key}_sm.webp${CB}`);
    }
  }
  // If resuming a crawl, preload combat background + all combatant sprites
  const combatData = result.data.combatData as ServerCombatState | null;
  const crawlData = result.data.crawlData as CrawlData | null;
  if (combatData?.status === 'active') {
    const encounterIdx = crawlData?.run?.encounterIndex ?? 0;
    const bgKey = crawlData?.config?.encounters?.[encounterIdx]?.bgKey ?? crawlData?.config?.bgKey;
    if (bgKey) {
      const bg = BACKGROUNDS[bgKey];
      if (bg) preloadUrls.push(`${base}${bg.path}`);
    }
    for (const c of combatData.combatants) {
      if (!c.isAlive) continue;
      const config = ENEMY_SPRITES[c.spriteKey] ?? PLAYER_SPRITES[c.spriteKey];
      if (config) {
        preloadUrls.push(`${base}${config.smPath}`);
        if (config.actionSmPath) preloadUrls.push(`${base}${config.actionSmPath}`);
      }
    }
  }
  await preloadImages(preloadUrls);

  // Hide loading screen, reveal nav bar
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.classList.add('hidden');
  }
  document.getElementById('nav-bar')?.classList.add('ready');

  // Listen for Phaser init request from CombatScreen
  EventBus.on('INIT_PHASER', (sceneData: unknown) => {
    initializePhaser(sceneData as CombatSceneData);
  });

  // Initialize UI (CombatScreen subscribes to state and decides what to show)
  initializeUI();

  console.log('[Main] Game initialized');

  // ── Phase 2: Background preload (non-blocking, after UI is up) ──
  // Only small files — icons used across inventory, party, tooltips (~30KB total)
  const iconUrls = [
    // Generic family fallbacks
    'icon_sword', 'icon_hammer', 'icon_dagger', 'icon_bow', 'icon_staff', 'icon_wand',
    'icon_buckler', 'icon_amulet', 'icon_ring',
    // T7/entry-tier weapon + armor icons (first ones players see)
    'icon_item_shortsword', 'icon_item_cudgel', 'icon_item_ashwood_staff',
    'icon_item_shiv', 'icon_item_hazel_wand', 'icon_item_wraps',
    'icon_item_shortbow', 'icon_item_paper_fan',
    'icon_item_breastplate', 'icon_item_hide_jerkin', 'icon_item_apprentice_robe',
    'icon_item_spun_shadeweave',
  ].map(icon => `${base}sprites/processed/icons/${icon}_sm.webp${CB}`);
  const elementIconUrls = [
    'fire', 'water', 'lightning', 'nature', 'wind', 'earth',
    'holy', 'dark', 'astral', 'steel', 'brute', 'finesse',
  ].map(t => `${base}sprites/processed/icons/elements/icon_element_${t}_sm.webp${CB}`);
  iconUrls.push(...elementIconUrls);
  preloadImages(iconUrls); // fire-and-forget, no await
}

// Shared initial scene data — CombatScene reads this via getSceneData()
let pendingSceneData: CombatSceneData | null = null;

/** Exported so CombatScene can read initial data on first create. */
export function getPendingSceneData(): CombatSceneData | null {
  const data = pendingSceneData;
  pendingSceneData = null;
  return data;
}

function initializePhaser(sceneData?: CombatSceneData): void {
  const canvasContainer = document.getElementById('phaser-canvas');
  if (!canvasContainer) {
    console.error('[Main] Phaser canvas container not found');
    return;
  }

  if (phaserGame) {
    // Already initialized — set pending data and restart the scene
    if (sceneData) pendingSceneData = sceneData;
    const scene = phaserGame.scene.getScene('CombatScene');
    if (scene) {
      scene.scene.restart();
    }
    return;
  }

  if (phaserInitPending) {
    // Already waiting for container to be laid out
    if (sceneData) pendingSceneData = sceneData;
    return;
  }

  // Store scene data for CombatScene to read on first create
  if (sceneData) {
    pendingSceneData = sceneData;
  }

  // Ensure container is visible so we get real dimensions
  canvasContainer.style.display = '';
  const battleContainer = document.getElementById('battle-container');
  if (battleContainer) battleContainer.style.display = '';

  // Full-screen canvas: size to the entire battle container
  // Require minimum 64px to avoid WebGL framebuffer errors on tiny contexts
  const MIN_DIM = 64;
  const width = canvasContainer.clientWidth;
  const height = canvasContainer.clientHeight;

  if (width < MIN_DIM || height < MIN_DIM) {
    // Container not laid out yet — defer until next frame
    console.warn(`[Main] Canvas container too small (${width}x${height}), deferring Phaser init`);
    phaserInitPending = true;
    requestAnimationFrame(() => {
      phaserInitPending = false;
      initializePhaser(sceneData);
    });
    return;
  }

  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: 'phaser-canvas',
    width: width,
    height: height,
    backgroundColor: '#121830',
    scene: [CombatScene],
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    render: {
      pixelArt: false,
      antialias: true,
      roundPixels: false,
    },
  };

  phaserGame = new Phaser.Game(config);
  console.log(`[Main] Phaser game initialized (${width}x${height})`);

  // Phaser's scale mode is RESIZE, which auto-handles canvas resizing.
  // No ResizeObserver needed — it would duplicate Phaser's built-in handling
  // and cause WebGL Framebuffer errors when PostFX RenderTargets resize.
}

function initializeUI(): void {
  // Initialize toast manager and tooltip
  ToastManager.initialize();
  Tooltip.initialize();

  // Initialize screen router + nav bar badges
  ScreenRouter.initialize();
  NavBar.initialize();

  // Register combat screen (pre-built, persistent, uses existing DOM)
  const combatScreen = new CombatScreen();
  combatScreen.initialize();
  ScreenRouter.registerInstance('combat', combatScreen);

  // Register main menu screen
  ScreenRouter.register('main-menu', () => {
    const screen = new MainMenuScreen();
    return screen;
  });

  ScreenRouter.register('party', () => new PartyScreen());

  // Register crawls browser screen
  ScreenRouter.register('crawls', () => new CrawlsBrowserScreen());

  // Register soulloom screen
  ScreenRouter.register('soulloom', () => new SoulloomScreen());

  // Register shop screen
  ScreenRouter.register('shop', () => new ShopScreen());

  // Register crafting screen
  ScreenRouter.register('crafting', () => new CraftingScreen());

  // Register crawl detail screen
  ScreenRouter.register('crawl-detail', () => new CrawlDetailScreen());

  // Campaign screen removed — campaign content now rendered inside CrawlsBrowserScreen

  // Register hero select screen
  ScreenRouter.register('hero-select', () => new HeroSelectScreen());

  // Register inspect screen (social presence)
  ScreenRouter.register('inspect', () => new InspectScreen());

  // Register codex screen
  ScreenRouter.register('codex', () => new CodexScreen());

  // Resume combat if there's an active session, otherwise go to main menu
  const combatData = StateManager.get('combatData') as { status?: string } | null | undefined;
  if (combatData?.status === 'active') {
    ScreenRouter.navigate('combat');
  } else {
    ScreenRouter.navigate('main-menu');
  }
}

function showError(message: string): void {
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.innerHTML = `
      <div class="loading-bg"></div>
      <div class="loading-content">
        <img src="${import.meta.env.BASE_URL}sprites/processed/images/fatewoven_logo_sm.webp" alt="Fatewoven" class="loading-logo" style="animation: none; filter: drop-shadow(0 0 20px rgba(255, 68, 68, 0.3));" />
        <p style="color: var(--color-danger); font-size: 14px;">${message}</p>
        <button onclick="location.reload()" class="combat-btn" style="margin-top: 8px; padding: 10px 32px;">
          Retry
        </button>
      </div>
    `;
  }
}

// Start the game
initializeGame().catch((error) => {
  console.error('[Main] Fatal error:', error);
  showError('Failed to start game. Please refresh.');
});
